import SpaceBetween from '@risk-smart/themed-cloudscape-components/space-between';
import Button from '@risksmart-app/components/src/button';
import { PageNotFound } from '@risksmart-app/components/src/errors/errors';
import { useGetGuidParam } from '@risksmart-app/components/src/routes/routes.utils';
import type { ParentIssueType } from '@risksmart-app/domain/src/types/consts';
import { ParentTypes as Parent_Type_Enum } from '@risksmart-app/domain/src/types/consts/parent-type';
import type { FC } from 'react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import type { ObjectToolbarConfig } from 'src/blocks/object-toolbar';
import { ObjectToolbar } from 'src/blocks/object-toolbar';
import { useObjectToolbarFormStore } from 'src/blocks/object-toolbar/useObjectToolbarFormStore';
import ControlledTabs from 'src/components/controlled-tabs';
import DeleteModal from 'src/components/delete-modal/DeleteModal';
import TabSettingsModal from 'src/components/tab-settings-modal';
import { useGetIssueById } from 'src/hooks/queries';
import { PageLayout } from 'src/layouts';
import { Permission } from 'src/rbac/Permission';
import {
  useGetDetailParentPath,
  useGetDetailPath,
} from 'src/routes/useGetDetailParentPath';

import { useDeleteIssues } from '@/hooks/mutations/issue';
import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { useDeleteResultNotification } from '@/hooks/useMutationResultNotification';
import useTabs from '@/hooks/useTabs';
import { getFriendlyId } from '@/utils/friendlyId';
import { IssueTypeMapping } from '@/utils/issueVariantUtils';

import useExporter from './useExporter';

type Props = {
  activeTabId:
    | 'actions'
    | 'assessment'
    | 'causes'
    | 'consequences'
    | 'details'
    | 'linkedItems'
    | 'notificationHistory'
    | 'updates';
  showDeleteButton?: boolean;
  issueType: ParentIssueType;
};

const Page: FC<Props> = ({ activeTabId, showDeleteButton, issueType }) => {
  const navigate = useNavigate();
  const { t } = useTranslation(['common']);
  const objectToolbarEnabled = useIsFeatureFlagEnabled('dynamic_forms');
  // Form-editor opener bridged up from the active tab's FormMenu (relocate mode).
  // Present only on the details tab when dynamic_forms are on.
  const formConfig = useObjectToolbarFormStore((s) => s.formConfigState);
  const [showTabSettings, setShowTabSettings] = useState(false);

  const issueId = useGetGuidParam('issueId');
  const { data, error, loading } = useGetIssueById({
    queryArgs: { id: issueId! },
    shouldSkip: !issueId,
  });
  if (error) {
    throw error;
  }
  const detailsPath = useGetDetailPath(issueId);
  const parentPath = useGetDetailParentPath(issueId);

  const issue = data?.issue?.[0];
  if (!loading && issueType !== issue?.Type) {
    throw new PageNotFound(`${issueType} with id ${issueId} not found`);
  }
  const issueTypeMap = IssueTypeMapping[issueType];
  const { t: st } = useTranslation(['common'], {
    keyPrefix: issueTypeMap.taxonomy,
  });
  const defaultTitle = st('fallback_title');
  const [exportItem, { loading: exporting }] = useExporter(issueId, issueType);
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const { deleteIssues, loading: deleteLoading } = useDeleteIssues();

  const tabs = useTabs({
    parentType: Parent_Type_Enum.Issue,
    parent: issue,
    hrefRoot: detailsPath,
    disabled: false,
    issueSubType: (issue?.Type as ParentIssueType) ?? Parent_Type_Enum.Issue,
  });

  const onDelete = useDeleteResultNotification({
    entityName: st('entity_name'),
    asyncAction: async () => {
      // Delete by the ROUTE id, not `issue.Id` — see the same change on the
      // indicator detail page (ENG-616). `issue` is undefined until
      // `useGetIssueById` resolves, but the Delete button renders straight away,
      // so guarding on it turned a confirm clicked inside the read window into a
      // silent no-op plus an unhandled `Error: No issue found`. That is what
      // failed `issues.spec.ts` "Can delete an issue" under V3.
      await deleteIssues({
        Ids: [issueId],
      });
      navigate(parentPath);

      return true;
    },
  });

  if (data?.issue.length === 0) {
    throw new PageNotFound(`${st('entity_name')} with id ${issueId} not found`);
  }
  const title = issue && issue.Title;
  const counter =
    issue &&
    `(${getFriendlyId(issue?.Type ?? Parent_Type_Enum.Issue, issue.SequentialId)})`;

  // Object-level toolbar (CUS-156) — flag-gated. Relocates Export + Delete into
  // the toolbar's overflow ⋮ menu and surfaces the Save disc / Cancel X (save
  // state bridged from the active tab's form via useObjectToolbarFormStore).
  // Delete tracks `showDeleteButton` — the toolbar's danger slot only supports a
  // permission gate, so the route-level boolean (delete shows on the details tab
  // only) is applied by conditionally including the danger action (parity).
  const toolbarConfig: ObjectToolbarConfig = useMemo(
    () => ({
      header: {
        // `||` (not `??`) — an empty-string Title must fall back to defaultTitle.
        title: issue?.Title || defaultTitle,
        // Pass the already-parenthesised counter (rendered verbatim).
        counter: counter || undefined,
      },
      parentObject: issue,
      save: true,
      actions: {
        overflow: [
          {
            id: 'edit',
            label: t('edit'),
            actions: [
              ...(formConfig
                ? [
                    {
                      id: 'edit-form',
                      label: t('edit_form'),
                      iconName: 'file-06' as const,
                      onClick: formConfig.openConfigure,
                    },
                  ]
                : []),
              {
                id: 'edit-tabs',
                label: t('edit_tabs'),
                iconName: 'layers-three-01',
                onClick: () => setShowTabSettings(true),
              },
            ],
          },
        ],
        export: [
          {
            id: 'export',
            actions: [
              {
                id: 'export',
                label: t('export.standard_pdf'),
                iconName: 'download-01',
                onClick: exportItem,
                disabled: exporting,
              },
            ],
          },
        ],
        danger: showDeleteButton
          ? [
              {
                id: 'delete',
                label: st('delete_button'),
                iconName: 'trash-01',
                permission: 'delete:issue',
                onClick: () => setIsDeleteModalVisible(true),
              },
            ]
          : undefined,
      },
    }),
    [
      issue,
      defaultTitle,
      counter,
      t,
      st,
      exportItem,
      exporting,
      formConfig,
      showDeleteButton,
    ]
  );

  return (
    <PageLayout
      title={title}
      meta={{ title: defaultTitle }}
      counter={counter}
      headerOverride={
        objectToolbarEnabled ? (
          <ObjectToolbar config={toolbarConfig} />
        ) : undefined
      }
      actions={
        <SpaceBetween direction={'horizontal'} size={'xs'}>
          <Button
            iconName={'download'}
            disabled={exporting}
            onClick={exportItem}
          >
            {t('export.export')}
          </Button>
          {showDeleteButton && (
            <Permission permission={'delete:issue'} parentObject={issue}>
              <Button
                variant={'normal'}
                formAction={'none'}
                onClick={() => {
                  setIsDeleteModalVisible(true);
                }}
              >
                {st('delete_button')}
              </Button>
            </Permission>
          )}
        </SpaceBetween>
      }
    >
      <ControlledTabs
        activeTabId={activeTabId}
        tabs={tabs}
        variant={'container'}
        parentType={Parent_Type_Enum.Issue}
        parent={issue}
        disableSettings={objectToolbarEnabled}
      />
      <TabSettingsModal
        parentType={Parent_Type_Enum.Issue}
        isVisible={showTabSettings}
        onDismiss={() => setShowTabSettings(false)}
        parent={issue}
      />
      <DeleteModal
        loading={deleteLoading}
        isVisible={isDeleteModalVisible}
        header={t('delete')}
        onDelete={onDelete}
        onDismiss={() => setIsDeleteModalVisible(false)}
      >
        {st('confirm_delete_message')}
      </DeleteModal>
    </PageLayout>
  );
};

export default Page;
